/**
 * ActivityTracker
 * Tracks user activity (mouse, keyboard) and sends to backend with debounce
 * Platform-aware: Works on web, React Native, and Node.js
 * Matches the test app's activity tracking implementation
 */
export class ActivityTracker {
    /**
     * Create ActivityTracker
     * @param presenceClient - PresenceClient instance
     * @param debounceMs - Debounce delay in milliseconds (default: 5000 = 5 seconds) - DEPRECATED
     * @param highSignificanceThrottleMs - Throttle delay for keyboard/clicks (default: 2000 = 2 seconds)
     * @param lowSignificanceThrottleMs - Throttle delay for mouse/scroll (default: 5000 = 5 seconds)
     * @param platform - Platform type (default: auto-detect)
     */
    constructor(presenceClient, debounceMs = 5000, highSignificanceThrottleMs = 2000, lowSignificanceThrottleMs = 5000, platform = 'web') {
        this.presenceClient = presenceClient;
        this.debounceMs = debounceMs;
        this.highSignificanceThrottleMs = highSignificanceThrottleMs;
        this.lowSignificanceThrottleMs = lowSignificanceThrottleMs;
        this.platform = platform;
        this.debounceTimer = null;
        this.throttleTimer = null;
        this.lastActivityTime = Date.now();
        this.lastServerSyncTime = 0;
        this.isActive = false;
        this.activityHandler = null;
        this.highSignificanceHandler = null;
        this.lowSignificanceHandler = null;
        /**
         * Handle activity event (internal) - DEPRECATED, kept for React Native compatibility
         * Debounces calls to recordActivity
         * @private
         */
        this.handleActivity = () => {
            this.lastActivityTime = Date.now();
            // Clear existing debounce timer
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }
            // Set new debounce timer
            this.debounceTimer = setTimeout(async () => {
                try {
                    await this.presenceClient.recordActivity();
                    console.log('[ActivityTracker] Activity recorded at', new Date().toISOString());
                }
                catch (error) {
                    console.error('[ActivityTracker] Failed to record activity:', error);
                }
            }, this.debounceMs);
        };
        /**
         * Handle high-significance activity (keyboard, clicks)
         * Updates local timestamp immediately, throttles server sync
         * @private
         */
        this.handleHighSignificanceActivity = () => {
            // ALWAYS update local timestamp immediately (for idle detection)
            this.lastActivityTime = Date.now();
            // Throttle server sync
            this.throttleServerSync(this.highSignificanceThrottleMs);
        };
        /**
         * Handle low-significance activity (mouse movement, scroll)
         * Updates local timestamp immediately, throttles server sync with longer delay
         * @private
         */
        this.handleLowSignificanceActivity = () => {
            // ALWAYS update local timestamp immediately (for idle detection)
            this.lastActivityTime = Date.now();
            // Throttle server sync with longer delay
            this.throttleServerSync(this.lowSignificanceThrottleMs);
        };
        /**
         * Handle visibility change event
         * Treats becoming visible as high-significance activity
         * @private
         */
        this.handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                this.handleHighSignificanceActivity();
            }
        };
        // Auto-detect platform if not specified
        if (!platform) {
            this.platform = this.detectPlatform();
        }
    }
    /**
     * Auto-detect the current platform
     * @private
     */
    detectPlatform() {
        // Check for React Native
        if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
            return 'react-native';
        }
        // Check for browser environment
        if (typeof window !== 'undefined' && typeof document !== 'undefined') {
            return 'web';
        }
        // Default to Node.js
        return 'node';
    }
    /**
     * Start activity tracking
     * Begins listening to platform-specific activity events
     */
    start() {
        if (this.isActive) {
            console.warn('[ActivityTracker] Already tracking');
            return;
        }
        console.log(`[ActivityTracker] Starting on platform: ${this.platform}`);
        if (this.platform === 'web') {
            this.startWebTracking();
        }
        else if (this.platform === 'react-native') {
            this.startReactNativeTracking();
        }
        // Node.js doesn't need activity tracking (server-side)
        this.isActive = true;
    }
    /**
     * Stop activity tracking
     * Removes all event listeners and clears pending timers
     */
    stop() {
        if (!this.isActive) {
            return;
        }
        console.log('[ActivityTracker] Stopping');
        if (this.platform === 'web') {
            this.stopWebTracking();
        }
        else if (this.platform === 'react-native') {
            this.stopReactNativeTracking();
        }
        // Clear any pending timers
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        if (this.throttleTimer) {
            clearTimeout(this.throttleTimer);
            this.throttleTimer = null;
        }
        this.isActive = false;
    }
    /**
     * Throttle server synchronization
     * Ensures we send updates at most once per interval
     * @private
     */
    throttleServerSync(throttleMs) {
        const now = Date.now();
        const timeSinceLastSync = now - this.lastServerSyncTime;
        // If enough time has passed, sync immediately
        if (timeSinceLastSync >= throttleMs) {
            this.syncActivityToServer();
            return;
        }
        // Otherwise, schedule a sync for the remaining time
        if (!this.throttleTimer) {
            const remainingTime = throttleMs - timeSinceLastSync;
            this.throttleTimer = setTimeout(() => {
                this.syncActivityToServer();
            }, remainingTime);
        }
    }
    /**
     * Send activity to server
     * @private
     */
    async syncActivityToServer() {
        this.lastServerSyncTime = Date.now();
        this.throttleTimer = null;
        try {
            await this.presenceClient.recordActivity();
            console.log('[ActivityTracker] Activity synced to server at', new Date().toISOString());
        }
        catch (error) {
            console.error('[ActivityTracker] Failed to sync activity to server:', error);
        }
    }
    /**
     * Start web platform tracking
     * Listens to high-significance (keyboard, clicks) and low-significance (mouse, scroll) events
     * Uses capture phase to ensure events are caught before stopPropagation() calls
     * @private
     */
    startWebTracking() {
        if (typeof window === 'undefined') {
            console.warn('[ActivityTracker] Web platform not available');
            return;
        }
        console.log('[ActivityTracker] Starting web event listeners (capture phase)');
        // Use capture phase to catch events before any stopPropagation() calls in React components
        const captureOptions = { capture: true };
        // High-significance events (definite user activity)
        window.addEventListener('keydown', this.handleHighSignificanceActivity, captureOptions);
        window.addEventListener('keypress', this.handleHighSignificanceActivity, captureOptions);
        window.addEventListener('mousedown', this.handleHighSignificanceActivity, captureOptions);
        window.addEventListener('click', this.handleHighSignificanceActivity, captureOptions);
        window.addEventListener('touchstart', this.handleHighSignificanceActivity, captureOptions);
        window.addEventListener('pointerdown', this.handleHighSignificanceActivity, captureOptions);
        window.addEventListener('focus', this.handleHighSignificanceActivity, captureOptions);
        // Low-significance events (potential idle movement)
        window.addEventListener('mousemove', this.handleLowSignificanceActivity, captureOptions);
        window.addEventListener('pointermove', this.handleLowSignificanceActivity, captureOptions);
        window.addEventListener('scroll', this.handleLowSignificanceActivity, captureOptions);
        // Document-level events
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
    /**
     * Stop web platform tracking
     * Removes all web event listeners
     * @private
     */
    stopWebTracking() {
        if (typeof window === 'undefined')
            return;
        console.log('[ActivityTracker] Removing web event listeners');
        // Must use same options as addEventListener (capture: true)
        const captureOptions = { capture: true };
        // High-significance events
        window.removeEventListener('keydown', this.handleHighSignificanceActivity, captureOptions);
        window.removeEventListener('keypress', this.handleHighSignificanceActivity, captureOptions);
        window.removeEventListener('mousedown', this.handleHighSignificanceActivity, captureOptions);
        window.removeEventListener('click', this.handleHighSignificanceActivity, captureOptions);
        window.removeEventListener('touchstart', this.handleHighSignificanceActivity, captureOptions);
        window.removeEventListener('pointerdown', this.handleHighSignificanceActivity, captureOptions);
        window.removeEventListener('focus', this.handleHighSignificanceActivity, captureOptions);
        // Low-significance events
        window.removeEventListener('mousemove', this.handleLowSignificanceActivity, captureOptions);
        window.removeEventListener('pointermove', this.handleLowSignificanceActivity, captureOptions);
        window.removeEventListener('scroll', this.handleLowSignificanceActivity, captureOptions);
        // Document-level events
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    /**
     * Start React Native platform tracking
     * Uses AppState to detect app activity
     * @private
     */
    startReactNativeTracking() {
        try {
            // Dynamically import AppState for React Native
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const { AppState } = require('react-native');
            this.activityHandler = AppState.addEventListener('change', (nextAppState) => {
                if (nextAppState === 'active') {
                    this.handleActivity();
                }
            });
            console.log('[ActivityTracker] Started React Native AppState tracking');
        }
        catch (error) {
            console.warn('[ActivityTracker] React Native AppState not available:', error);
        }
    }
    /**
     * Stop React Native platform tracking
     * Removes AppState listener
     * @private
     */
    stopReactNativeTracking() {
        if (this.activityHandler) {
            try {
                // Remove the subscription
                if (typeof this.activityHandler === 'function') {
                    // In newer React Native versions, addEventListener returns a remove function
                    this.activityHandler();
                }
                else if ('remove' in this.activityHandler) {
                    // Older versions have a remove method
                    this.activityHandler.remove();
                }
                this.activityHandler = null;
                console.log('[ActivityTracker] Stopped React Native tracking');
            }
            catch (error) {
                console.warn('[ActivityTracker] Failed to stop React Native tracking:', error);
            }
        }
    }
    /**
     * Get the timestamp of the last detected activity
     * @returns Timestamp in milliseconds
     */
    getLastActivityTime() {
        return this.lastActivityTime;
    }
    /**
     * Reset last activity time to now
     * Used after screen unlock to prevent immediate re-idle
     */
    resetLastActivity() {
        this.lastActivityTime = Date.now();
        console.log('[ActivityTracker] Last activity time reset to now');
    }
    /**
     * Manually trigger activity recording
     * Bypasses debounce and sends immediately
     */
    async triggerActivity() {
        try {
            await this.presenceClient.recordActivity();
            this.lastActivityTime = Date.now();
            console.log('[ActivityTracker] Activity triggered manually');
        }
        catch (error) {
            console.error('[ActivityTracker] Failed to trigger activity:', error);
            throw error;
        }
    }
    /**
     * Update the debounce delay
     * @param ms - New debounce delay in milliseconds
     */
    setDebounce(ms) {
        if (ms < 1000) {
            console.warn('[ActivityTracker] Debounce too short, using minimum 1000ms');
            ms = 1000;
        }
        this.debounceMs = ms;
        console.log(`[ActivityTracker] Debounce updated to ${ms}ms`);
    }
    /**
     * Get current debounce setting
     * @returns Debounce delay in milliseconds
     */
    getDebounce() {
        return this.debounceMs;
    }
    /**
     * Check if activity tracking is currently active
     * @returns True if tracking, false otherwise
     */
    isTracking() {
        return this.isActive;
    }
    /**
     * Get the current platform
     * @returns Platform type
     */
    getPlatform() {
        return this.platform;
    }
    /**
     * Update high-significance throttle delay
     * @param ms - New throttle delay in milliseconds
     */
    setHighSignificanceThrottle(ms) {
        if (ms < 500) {
            console.warn('[ActivityTracker] High-significance throttle too short, using minimum 500ms');
            ms = 500;
        }
        this.highSignificanceThrottleMs = ms;
        console.log(`[ActivityTracker] High-significance throttle updated to ${ms}ms`);
    }
    /**
     * Update low-significance throttle delay
     * @param ms - New throttle delay in milliseconds
     */
    setLowSignificanceThrottle(ms) {
        if (ms < 1000) {
            console.warn('[ActivityTracker] Low-significance throttle too short, using minimum 1000ms');
            ms = 1000;
        }
        this.lowSignificanceThrottleMs = ms;
        console.log(`[ActivityTracker] Low-significance throttle updated to ${ms}ms`);
    }
}
//# sourceMappingURL=ActivityTracker.js.map